import { Section, CodeBlock, InfoBox } from "@/components"

export function NextShadcnComponentsPage() {
  return (
    <div className="space-y-10">
      <div className="space-y-4">
        <h1 className="text-4xl font-bold">Components</h1>
        <p className="text-lg text-muted-foreground max-w-4xl leading-relaxed">
          Explore the Shadcn UI components and learn how to use them in Next.js with both 
          Server and Client Components.
        </p>
      </div>

      <Section title="Shadcn UI in Next.js" description="Best practices">
        <div className="space-y-6">
          <p className="text-muted-foreground leading-relaxed">
            Shadcn UI components are Client Components by default since they use interactivity. 
            They can be used within Server Components as children.
          </p>
          
          <InfoBox type="info" title="Component Installation">
            All Shadcn UI components include <code className="bg-muted px-2 py-1 rounded">"use client"</code> 
            directive automatically when needed.
          </InfoBox>
        </div>
      </Section>

      <Section title="Using Components" description="In Server and Client contexts">
        <div className="space-y-6">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Server Component with Client UI</h3>
            <CodeBlock
              code={`// app/users/page.tsx (Server Component)
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"

async function getUsers() {
  const res = await fetch('https://api.example.com/users')
  return res.json()
}

export default async function UsersPage() {
  const users = await getUsers()

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Users</h1>
        <Button>Add User</Button>
      </div>
      
      <div className="grid gap-4">
        {users.map(user => (
          <Card key={user.id} className="p-6">
            <h2 className="text-xl font-semibold">{user.name}</h2>
            <p className="text-muted-foreground">{user.email}</p>
          </Card>
        ))}
      </div>
    </div>
  )
}`}
              language="typescript"
            />
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Interactive Client Component</h3>
            <CodeBlock
              code={`// components/AddUserForm.tsx
"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

export function AddUserForm() {
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const res = await fetch("/api/users", {
      method: "POST",
      body: JSON.stringify({ name, email })
    })
    // Handle response...
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="name">Name</Label>
        <Input
          id="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
      </div>
      <div>
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </div>
      <Button type="submit">Add User</Button>
    </form>
  )
}`}
              language="typescript"
            />
          </div>
        </div>
      </Section>

      <Section title="Component Library" description="Pre-installed components">
        <div className="space-y-6">
          <p className="text-muted-foreground leading-relaxed">
            The template comes with commonly used Shadcn UI components pre-installed.
          </p>

          <div className="grid md:grid-cols-3 gap-4">
            <ComponentListCard
              title="Forms"
              items={["Button", "Input", "Select", "Checkbox", "Switch", "Textarea", "Label"]}
            />
            <ComponentListCard
              title="Overlays"
              items={["Dialog", "Sheet", "Dropdown Menu", "Tooltip", "Popover", "Toast"]}
            />
            <ComponentListCard
              title="Layout"
              items={["Card", "Tabs", "Separator", "Skeleton", "Avatar", "Badge"]}
            />
          </div>
        </div>
      </Section>

      <Section title="Server Actions with Forms" description="Next.js 14 pattern">
        <div className="space-y-6">
          <p className="text-muted-foreground leading-relaxed">
            Use Server Actions for form mutations without needing API routes.
          </p>
          
          <CodeBlock
            code={`// app/users/actions.ts
"use server"

import { revalidatePath } from "next/cache"

export async function createUser(formData: FormData) {
  const name = formData.get("name") as string
  const email = formData.get("email") as string

  // Create user in database
  await db.user.create({
    data: { name, email }
  })

  // Revalidate the users page
  revalidatePath("/users")
}

// app/users/new/page.tsx
import { createUser } from "../actions"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

export default function NewUserPage() {
  return (
    <form action={createUser} className="space-y-4">
      <div>
        <Label htmlFor="name">Name</Label>
        <Input id="name" name="name" required />
      </div>
      <div>
        <Label htmlFor="email">Email</Label>
        <Input id="email" name="email" type="email" required />
      </div>
      <Button type="submit">Create User</Button>
    </form>
  )
}`}
            language="typescript"
          />
        </div>
      </Section>

      <div className="bg-primary/5 border border-primary/20 rounded-xl p-8 space-y-4">
        <h2 className="text-2xl font-bold">Component Best Practices</h2>
        <ul className="space-y-3 text-sm">
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Use Server Components by default, Client Components only when needed</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Shadcn UI components can be used in Server Components as children</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Use Server Actions for form mutations instead of API routes</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Keep interactive components small and focused</span>
          </li>
        </ul>
      </div>
    </div>
  )
}

function ComponentListCard({ 
  title, 
  items 
}: { 
  title: string
  items: string[]
}) {
  return (
    <div className="bg-card border rounded-lg p-4 space-y-3">
      <h4 className="font-semibold">{title}</h4>
      <ul className="space-y-2">
        {items.map((item, index) => (
          <li key={index} className="text-sm text-muted-foreground flex gap-2">
            <span className="text-primary">•</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

